import { useEffect, useState } from 'react';
import { CoachResponse, DbUser, Race, User, WorkoutData, WorkoutRecommendation } from './types';
import { generateWorkoutRecommendation } from './services/recommendations';
import { fetchStravaWorkouts, fetchOuraData } from './services/api';
import {
  CoachContext,
  MetricsSource,
  askCoach,
  buildCoachContext,
  coachResponseToRecommendation,
} from './services/aiCoach';
import { listRaces } from './services/races';
import { activitiesToWorkouts, listRecentActivities } from './services/activities';
import { fetchTerraData, handleTerraCallback, mergeWorkouts } from './services/terra';
import { fetchAppleHealthDay } from './services/appleHealth';
import {
  getTodaysRecommendation,
  listRecentRecommendations,
  markRecommendationCompleted,
  rateRecommendation,
  saveTodaysRecommendation,
  updateTodaysRecommendation,
} from './services/dailyRecommendation';
import {
  CheckInFeeling,
  DailyCheckIn,
  getTodaysCheckIn,
  saveTodaysCheckIn,
} from './services/checkIn';
import { fetchTrainingPlan } from './services/trainingPlan';
import { localDate } from './utils/date';
import { Settings } from './components/Settings';
import { AuthForm } from './components/AuthForm';
import { Onboarding } from './components/Onboarding';
import { LandingPage } from './components/LandingPage';
import { Dashboard } from './components/Dashboard';
import { DemoDay } from './components/DemoDay';
import { supabase, getValidStravaToken, getValidOuraToken } from './services/auth';
import { handleStravaCallback, handleOuraCallback } from './services/auth';

/** Metric sources that come from the athlete's own wearables. A plan built
    without one of these is worth regenerating once real data lands. */
const REAL_METRICS_SOURCES: string[] = ['oura', 'garmin-via-terra', 'apple-health'];

function App() {
  const [user, setUser] = useState<User | null>(null);
  const [recommendation, setRecommendation] = useState<WorkoutRecommendation | null>(null);
  const [races, setRaces] = useState<Race[]>([]);
  const [coachContext, setCoachContext] = useState<CoachContext | null>(null);
  const [coachIntro, setCoachIntro] = useState<string | null>(null);
  const [recommendationSource, setRecommendationSource] = useState<'ai' | 'rules'>('rules');
  const [checkIn, setCheckIn] = useState<DailyCheckIn | null>(null);
  const [awaitingCheckIn, setAwaitingCheckIn] = useState(false);
  const [todayRating, setTodayRating] = useState<'up' | 'down' | null>(null);
  const [todayCompleted, setTodayCompleted] = useState<boolean | null>(null);
  const [regenerating, setRegenerating] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showAuthForm, setShowAuthForm] = useState(false);
  // /demo is deep-linkable: a zero-signup sample day, no auth required.
  const [demoMode, setDemoMode] = useState(window.location.pathname === '/demo');

  const convertDbUserToFrontend = (dbUser: DbUser): User => ({
    id: dbUser.id,
    email: dbUser.email,
    name: dbUser.name || '',
    phoneNumber: dbUser.phone_number ?? undefined,
    weight: dbUser.weight ?? undefined,
    stravaToken: dbUser.strava_token ?? undefined,
    stravaRefreshToken: dbUser.strava_refresh_token ?? undefined,
    stravaTokenExpiry: dbUser.strava_token_expiry ?? undefined,
    ouraToken: dbUser.oura_token ?? undefined,
    ouraRefreshToken: dbUser.oura_refresh_token ?? undefined,
    ouraTokenExpiry: dbUser.oura_token_expiry ?? undefined,
    notificationPreferences: dbUser.notification_preferences,
    fitnessGoals: dbUser.fitness_goals,
    activityPreferences: dbUser.activity_preferences || [],
    weeklyTrainingVolume: dbUser.weekly_training_volume || 0,
    acceptedTerms: dbUser.accepted_terms,
    trainingDays: dbUser.training_days ?? undefined,
    typicalSessionMinutes: dbUser.typical_session_minutes ?? undefined,
    birthYear: dbUser.birth_year ?? undefined,
    injuryNotes: dbUser.injury_notes ?? undefined,
    lifeContext: dbUser.life_context ?? undefined,
    experienceLevel: dbUser.experience_level ?? undefined,
    maxHr: dbUser.max_hr ?? undefined,
    thresholdPace: dbUser.threshold_pace ?? undefined,
    trainingPlanIcalUrl: dbUser.training_plan_ical_url ?? undefined,
    terraUserId: dbUser.terra_user_id ?? undefined,
    terraProvider: dbUser.terra_provider ?? undefined,
    ingestKey: dbUser.ingest_key,
    created_at: dbUser.created_at,
    updated_at: dbUser.updated_at,
  });

  useEffect(() => {
    // Initialize Supabase session
    const initializeAuth = async () => {
      try {
        // Get the current session
        const { data: { session }, error } = await supabase.auth.getSession();
        if (error) throw error;

        // If we have a session, refresh it
        if (session) {
          const { error: refreshError } = await supabase.auth.refreshSession();
          if (refreshError) throw refreshError;
        }

        // Process any OAuth callback before loading the user, so the UI
        // renders with the freshly saved tokens. A successful callback
        // navigates away, in which case there is nothing left to load.
        const redirecting = await checkAuthCallbacks();
        if (!redirecting) {
          await checkUser();
        }
      } catch (err) {
        console.error('Error initializing auth:', err);
        setError('Authentication failed. Please try signing in again.');
        setIsAuthenticated(false);
        setLoading(false);
      }
    };

    initializeAuth();
  }, []);

  // Returns true when a callback was handled successfully and the page is
  // about to navigate to the post-connect location.
  const checkAuthCallbacks = async (): Promise<boolean> => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');
    const providerError = urlParams.get('error');

    if (providerError) {
      console.error('Authentication error:', providerError);
      setError(`The service reported an error while connecting: ${providerError}`);
      window.history.replaceState({}, '', '/');
      return false;
    }

    // Terra (Garmin etc.) returns user_id/resource rather than an OAuth code
    if (window.location.pathname.includes('/auth/terra/callback')) {
      try {
        await handleTerraCallback(urlParams);
        window.location.href = '/';
        return true;
      } catch (err) {
        console.error('Error handling Terra callback:', err);
        setError(err instanceof Error ? err.message : 'Failed to connect the wearable.');
        window.history.replaceState({}, '', '/');
        return false;
      }
    }

    if (!code || !state) return false;

    try {
      // Check if this is an Oura callback
      if (window.location.pathname.includes('/auth/oura/callback')) {
        await handleOuraCallback(code, state);
      } else {
        // Otherwise assume it's a Strava callback
        await handleStravaCallback(code, state);
      }
      return true;
    } catch (err) {
      console.error('Error handling auth callback:', err);
      setError(err instanceof Error ? err.message : 'Failed to connect the service. Please try again.');
      window.history.replaceState({}, '', '/');
      return false;
    }
  };

  const checkUser = async () => {
    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError) throw sessionError;

      if (!session?.user) {
        setIsAuthenticated(false);
        setLoading(false);
        return;
      }

      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('*')
        .eq('id', session.user.id)
        .maybeSingle();

      if (userError) throw userError;

      setIsAuthenticated(true);

      if (userData) {
        const frontendUser = convertDbUserToFrontend(userData);
        setUser(frontendUser);
        setShowOnboarding(false);
        fetchDailyRecommendation(frontendUser);
      } else {
        setShowOnboarding(true);
      }
    } catch (err) {
      console.error('Error checking user:', err);
      setError('Failed to load user data');
    } finally {
      setLoading(false);
    }
  };

  const handleAuthSuccess = async () => {
    try {
      setLoading(true);
      const { data: { user: authUser }, error: authError } = await supabase.auth.getUser();
      if (authError) throw authError;

      if (!authUser) {
        throw new Error('No authenticated user found');
      }

      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('*')
        .eq('id', authUser.id)
        .maybeSingle();

      if (userError) throw userError;

      setIsAuthenticated(true);
      setShowAuthForm(false);

      if (userData) {
        // Existing user - go straight to dashboard
        const frontendUser = convertDbUserToFrontend(userData);
        setUser(frontendUser);
        setShowOnboarding(false);
        fetchDailyRecommendation(frontendUser);
      } else {
        // New user - show onboarding
        setShowOnboarding(true);
      }
    } catch (error) {
      console.error('Error in auth success:', error);
      setError('Authentication failed. Please try again.');
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  const handleOnboardingComplete = async (userData: Partial<User>) => {
    try {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (!authUser) throw new Error('No authenticated user found');

      // Weekly volume is estimated from schedule; real volume is learned
      // from actual training data over time.
      const estimatedVolume =
        userData.trainingDays && userData.typicalSessionMinutes
          ? Math.round((userData.trainingDays.length * userData.typicalSessionMinutes) / 60)
          : userData.weeklyTrainingVolume ?? 0;

      const dbData = {
        id: authUser.id,
        email: authUser.email,
        name: userData.name,
        phone_number: userData.phoneNumber,
        weight: userData.weight,
        notification_preferences: userData.notificationPreferences,
        fitness_goals: userData.fitnessGoals,
        activity_preferences: userData.activityPreferences,
        weekly_training_volume: estimatedVolume,
        accepted_terms: userData.acceptedTerms,
        training_days: userData.trainingDays ?? [],
        typical_session_minutes: userData.typicalSessionMinutes ?? null,
        birth_year: userData.birthYear ?? null,
      };

      const { data, error } = await supabase
        .from('users')
        .insert([dbData])
        .select()
        .single();

      if (error) throw error;

      const frontendUser = convertDbUserToFrontend(data);
      setUser(frontendUser);
      setShowOnboarding(false);
      fetchDailyRecommendation(frontendUser);
    } catch (err) {
      console.error('Error saving onboarding data:', err);
      setError('Failed to save your information. Please try again.');
    }
  };

  const handleOnboardingCancel = () => {
    setIsAuthenticated(false);
    setShowOnboarding(false);
  };

  const fetchDailyRecommendation = async (currentUser: User, force = false) => {
    try {
      if (!force) setLoading(true);
      setError(null);

      // Only fetch from services if tokens are available
      const stravaToken = currentUser.stravaToken
        ? await getValidStravaToken(
            currentUser.id,
            currentUser.stravaToken,
            currentUser.stravaRefreshToken,
            currentUser.stravaTokenExpiry
          )
        : null;

      const ouraToken = currentUser.ouraToken
        ? await getValidOuraToken(
            currentUser.id,
            currentUser.ouraToken,
            currentUser.ouraRefreshToken,
            currentUser.ouraTokenExpiry
          )
        : null;

      const [stravaWorkouts, manualActivities, ouraFetched, terraData, appleDay, userRaces, todayCheckIn, recentPlans, plannedWorkouts] = await Promise.all([
        stravaToken ? fetchStravaWorkouts(stravaToken) : ([] as WorkoutData[]),
        listRecentActivities().catch(() => []),
        ouraToken ? fetchOuraData(ouraToken) : null,
        currentUser.terraUserId
          ? fetchTerraData(currentUser.terraUserId).catch(() => null)
          : Promise.resolve(null),
        fetchAppleHealthDay().catch(() => null),
        listRaces().catch(() => [] as Race[]),
        getTodaysCheckIn().catch(() => null),
        listRecentRecommendations(7).catch(() => []),
        currentUser.trainingPlanIcalUrl
          ? fetchTrainingPlan(currentUser.trainingPlanIcalUrl).catch((e) => {
              console.warn('Training plan feed unavailable:', e);
              return null;
            })
          : Promise.resolve(null),
      ]);
      setRaces(userRaces);
      setCheckIn(todayCheckIn);

      // Recovery source priority: Oura, then Terra-connected wearable
      // (Garmin etc.), then Apple Health relay. If every source comes back
      // empty the day simply has no metrics — the coach is told that
      // instead of being handed invented ones.
      const ouraData = ouraFetched
        ?? (terraData ? { sleep: terraData.sleep, recovery: terraData.recovery } : null)
        ?? appleDay
        ?? null;

      // Strava + Terra + manual log form one training week (Terra entries
      // that duplicate Strava ones are dropped). An empty week is an empty
      // week; it is never padded with a placeholder session.
      const workoutData: WorkoutData[] = mergeWorkouts(
        [...stravaWorkouts, ...activitiesToWorkouts(manualActivities)],
        terraData?.workouts ?? []
      );

      const hasWearableConnection =
        !!currentUser.ouraToken || !!currentUser.terraUserId;

      const metricsSource: MetricsSource = ouraFetched
        ? 'oura'
        : terraData
          ? 'garmin-via-terra'
          : appleDay
            ? 'apple-health'
            : hasWearableConnection
              ? 'wearable-read-failed'
              : 'no-wearable-connected';

      const context = buildCoachContext(
        currentUser,
        userRaces,
        workoutData,
        ouraData?.sleep ?? null,
        ouraData?.recovery ?? null,
        metricsSource,
        todayCheckIn,
        recentPlans,
        plannedWorkouts
      );
      setCoachContext(context);

      // Today's plan is generated once and reused across page loads —
      // stable guidance, one model call per day. Regenerate only on
      // demand (force) or when the stored plan was built without real
      // metrics and real wearable data has since arrived (a token refresh
      // losing a race is enough to produce a metric-less plan).
      const stored = force ? null : await getTodaysRecommendation().catch(() => null);
      const storedHadRealMetrics =
        !!stored?.metrics_source && REAL_METRICS_SOURCES.includes(stored.metrics_source);
      const storedIsUsable =
        stored && (storedHadRealMetrics || !REAL_METRICS_SOURCES.includes(metricsSource));

      if (stored && storedIsUsable) {
        setRecommendation(stored.recommendation);
        setCoachIntro(stored.coach_intro);
        setRecommendationSource(stored.source);
        setTodayRating(stored.rating);
        setTodayCompleted(stored.completed);
        return;
      }
      setTodayRating(null);
      setTodayCompleted(null);

      // Feel-first: before the day's first plan is generated, ask how the
      // athlete feels so the coach can weigh it. Checking in (or skipping)
      // re-enters here with force=true, which bypasses this gate.
      if (!force && !todayCheckIn) {
        setAwaitingCheckIn(true);
        setRecommendation(null);
        return;
      }
      setAwaitingCheckIn(false);

      // Ask the AI coach first; fall back to the rules engine if it's
      // unavailable or errors.
      try {
        const coach = await askCoach(context);
        const rec = coachResponseToRecommendation(coach);
        setRecommendation(rec);
        setCoachIntro(coach.reply);
        setRecommendationSource('ai');
        await saveTodaysRecommendation({
          source: 'ai',
          metricsSource,
          recommendation: rec,
          coachIntro: coach.reply,
        }).catch((e) => console.error('Failed to store recommendation:', e));
      } catch (coachError) {
        console.warn('AI coach unavailable, using rules baseline:', coachError);
        const rec = generateWorkoutRecommendation(
          currentUser,
          workoutData,
          ouraData?.sleep ?? null,
          ouraData?.recovery ?? null,
          todayCheckIn,
          context.externalPlan?.today.find((p) => !p.completed) ?? null
        );
        setRecommendation(rec);
        setCoachIntro(null);
        setRecommendationSource('rules');
        await saveTodaysRecommendation({
          source: 'rules',
          metricsSource,
          recommendation: rec,
          coachIntro: null,
        }).catch((e) => console.error('Failed to store recommendation:', e));
      }
    } catch (err) {
      console.error('Error fetching recommendation:', err);
      // The load failed, so nothing about today is known: fall back to the
      // rules engine with no history and no metrics rather than with
      // invented ones.
      const fallbackRecommendation = generateWorkoutRecommendation(
        currentUser,
        [],
        null,
        null
      );
      setRecommendation(fallbackRecommendation);
    } finally {
      setLoading(false);
    }
  };

  if (demoMode) {
    return (
      <DemoDay
        onExit={() => {
          setDemoMode(false);
          window.history.replaceState({}, '', '/');
        }}
        onSignUp={() => {
          setDemoMode(false);
          window.history.replaceState({}, '', '/');
          sessionStorage.setItem('authMode', 'signup');
          setShowAuthForm(true);
        }}
      />
    );
  }

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-zinc-50 dark:bg-zinc-950">
        <div className="h-10 w-10 animate-spin rounded-full border-[3px] border-zinc-200 border-t-indigo-600 dark:border-zinc-800 dark:border-t-indigo-400"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    if (showAuthForm) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-zinc-50 px-4 dark:bg-zinc-950">
          <AuthForm onSuccess={handleAuthSuccess} onBack={() => setShowAuthForm(false)} />
        </div>
      );
    }
    return (
      <LandingPage
        onShowLogin={() => setShowAuthForm(true)}
        onTryDemo={() => {
          setDemoMode(true);
          window.history.pushState({}, '', '/demo');
        }}
      />
    );
  }

  if (showOnboarding) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-zinc-50 px-4 py-10 dark:bg-zinc-950">
        <Onboarding onComplete={handleOnboardingComplete} onCancel={handleOnboardingCancel} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-zinc-50 px-4 dark:bg-zinc-950">
        <div className="card w-full max-w-md p-8 text-center">
          <p className="text-lg font-bold text-zinc-900 dark:text-white">Something went wrong</p>
          <p className="mt-2 text-sm leading-relaxed text-zinc-600 dark:text-zinc-400">{error}</p>
          <button onClick={() => setError(null)} className="btn-primary mt-6 w-full py-3">
            Continue
          </button>
        </div>
      </div>
    );
  }

  if (showSettings && user) {
    return (
      <Settings 
        user={user} 
        onUpdate={(updatedUser) => {
          setUser(updatedUser);
          setShowSettings(false);
          fetchDailyRecommendation(updatedUser);
        }} 
        onBack={() => setShowSettings(false)} 
      />
    );
  }

  if (!user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-zinc-50 dark:bg-zinc-950">
        <div className="h-10 w-10 animate-spin rounded-full border-[3px] border-zinc-200 border-t-indigo-600 dark:border-zinc-800 dark:border-t-indigo-400"></div>
      </div>
    );
  }

  const todayIso = () => localDate();

  const handleRate = (rating: 'up' | 'down' | null) => {
    setTodayRating(rating);
    rateRecommendation(todayIso(), rating).catch((e) =>
      console.error('Failed to save rating:', e)
    );
  };

  const handleMarkCompleted = (completed: boolean | null) => {
    setTodayCompleted(completed);
    markRecommendationCompleted(todayIso(), completed).catch((e) =>
      console.error('Failed to save completion:', e)
    );
  };

  const handleCoachUpdate = (coach: CoachResponse) => {
    const rec = coachResponseToRecommendation(coach);
    setRecommendation(rec);
    // Chat-adjusted plans survive refreshes too
    updateTodaysRecommendation(rec).catch((e) =>
      console.error('Failed to persist adjusted recommendation:', e)
    );
  };

  const handleRegenerate = async () => {
    if (!user || regenerating) return;
    setRegenerating(true);
    try {
      await fetchDailyRecommendation(user, true);
    } finally {
      setRegenerating(false);
    }
  };

  // Check-in (or skip) answered — generate the day's plan. force=true both
  // bypasses the check-in gate and skips the (nonexistent) stored plan.
  const handleCheckIn = async (feeling: CheckInFeeling, note: string | null) => {
    if (!user || regenerating) return;
    setAwaitingCheckIn(false);
    setCheckIn({ day: localDate(), feeling, note });
    setRegenerating(true);
    try {
      await saveTodaysCheckIn(feeling, note).catch((e) =>
        console.error('Failed to save check-in:', e)
      );
      await fetchDailyRecommendation(user, true);
    } finally {
      setRegenerating(false);
    }
  };

  const handleSkipCheckIn = async () => {
    if (!user || regenerating) return;
    setAwaitingCheckIn(false);
    setRegenerating(true);
    try {
      await fetchDailyRecommendation(user, true);
    } finally {
      setRegenerating(false);
    }
  };

  return (
    <Dashboard
      user={user}
      recommendation={recommendation}
      recommendationSource={recommendationSource}
      races={races}
      coachContext={coachContext}
      coachIntro={coachIntro}
      checkIn={checkIn}
      awaitingCheckIn={awaitingCheckIn}
      onCheckIn={handleCheckIn}
      onSkipCheckIn={handleSkipCheckIn}
      todayRating={todayRating}
      todayCompleted={todayCompleted}
      onRate={handleRate}
      onMarkCompleted={handleMarkCompleted}
      onCoachUpdate={handleCoachUpdate}
      onRegenerate={handleRegenerate}
      regenerating={regenerating}
      onSettingsClick={() => setShowSettings(true)}
    />
  );
}

export default App;